import type React from 'react';
import {useRef, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography} from '@mui/material';

type Props = {onUpload: (name: string, unit: string, currency: string, file: File) => Promise<unknown>; onClose: () => void};

/** Upload a series the customer licenses themself: a CSV with period (YYYY-MM) and value. Mount it to open it. */
export const UploadSeriesDialog: React.FC<Props> = ({onUpload, onClose}) => {
    const [name, setName] = useState('');
    const [unit, setUnit] = useState('');
    const [currency, setCurrency] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);
    const input = useRef<HTMLInputElement>(null);

    const submit = () => {
        if (!file) return;
        setBusy(true);
        onUpload(name.trim(), unit.trim(), currency.trim(), file).then(onClose).catch(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby="cp-upload-title">
            <DialogTitle id="cp-upload-title">Upload a market series</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    <TextField label="Name" required size="small" value={name} onChange={e => setName(e.target.value)}
                               placeholder="e.g. Pulp NBSK Europe" slotProps={{htmlInput: {maxLength: 200}}}/>
                    <Stack direction="row" gap={1}>
                        <TextField label="Unit" size="small" value={unit} onChange={e => setUnit(e.target.value)} sx={{flex: 1}}
                                   placeholder="USD per tonne" slotProps={{htmlInput: {maxLength: 60}}}/>
                        <TextField label="Currency" size="small" value={currency} onChange={e => setCurrency(e.target.value)}
                                   sx={{width: 110}} slotProps={{htmlInput: {maxLength: 3}}}/>
                    </Stack>
                    <Stack direction="row" gap={1} alignItems="center">
                        <Button variant="outlined" onClick={() => input.current?.click()}>Choose CSV</Button>
                        <Typography variant="body2" noWrap>{file?.name ?? 'No file chosen'}</Typography>
                        <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={e => setFile(e.target.files?.[0] ?? null)}/>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        Two columns: month (YYYY-MM or a date) and value, one row per month; a header row is fine.
                        The series is visible to this dataset only.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !file || !name.trim()}>Upload</Button>
            </DialogActions>
        </Dialog>
    );
};
